import type { ArtifactDropWire, AssetManifest, CardDropWire } from '@kenocyber/shared';
import { useEffect } from 'react';
import { staticAssetUrl } from '../game/assets';
import { rarityLabel, rarityPaint, rarityStyle } from './rarity';

/**
 * Announces an artifact the SERVER confirmed.
 *
 * It appears only when a checkpoint answers with a drop - never when the
 * client thinks it killed something. That is the whole design: the browser has
 * no loot seed, cannot roll, and would have nothing to announce. The longest a
 * confirmation can take is one checkpoint window, and nothing polls for it.
 *
 * Floating and non-interactive: it sits over the arena like every other panel,
 * so it never resizes or recreates the Phaser canvas, and it never swallows a
 * click meant for the HUD underneath.
 */

interface DropToastProps {
  readonly drops: readonly ArtifactDropWire[];
  readonly manifest: AssetManifest;
  readonly onDismiss: (dropId: string) => void;
}

/** Long enough to read the name and see the art; short enough to stay out of the way. */
const VISIBLE_MS = 6_000;

function Toast({
  drop,
  manifest,
  onDismiss,
}: {
  drop: ArtifactDropWire;
  manifest: AssetManifest;
  onDismiss: (dropId: string) => void;
}) {
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(drop.dropId), VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [drop.dropId, onDismiss]);

  const url = staticAssetUrl(manifest, drop.assetKey);

  return (
    <div
      className="drop-toast"
      // The same rarity border the inventory cell uses, from the same catalog:
      // a Prismatic drop is unmistakably a Prismatic drop.
      data-rarity={drop.rarity}
      data-paint={rarityPaint(drop.rarity)}
      data-testid={`drop-toast-${drop.definitionId}`}
      style={rarityStyle(drop.rarity)}
      role="status"
    >
      <span className="drop-toast-art">
        {url ? <img src={url} alt="" draggable={false} /> : <span aria-hidden="true">?</span>}
      </span>
      <span className="drop-toast-text">
        <strong data-testid="drop-toast-name">{drop.name}</strong>
        <span data-testid="drop-toast-rarity">{rarityLabel(drop.rarity)}</span>
        <span className="drop-toast-source">
          {drop.fromBoss ? 'Derrubado por um boss' : 'Artefato encontrado'}
        </span>
      </span>
      <button
        type="button"
        className="drop-toast-close"
        data-testid="drop-toast-close"
        aria-label={`Fechar aviso de ${drop.name}`}
        onClick={() => onDismiss(drop.dropId)}
      >
        ×
      </button>
    </div>
  );
}

export function DropToasts({ drops, manifest, onDismiss }: DropToastProps) {
  if (drops.length === 0) {
    return null;
  }
  return (
    <div className="drop-toasts" data-testid="drop-toasts" aria-live="polite">
      {drops.map((drop) => (
        // Keyed by the server's drop id: stable, unique per account, and
        // derived from the kill - so the same drop can never render twice.
        <Toast key={drop.dropId} drop={drop} manifest={manifest} onDismiss={onDismiss} />
      ))}
    </div>
  );
}

/**
 * **Anuncia uma CARTA que o servidor confirmou** (gate V28H.E).
 *
 * ## Porque vive aqui, e porque é só texto
 *
 * Aqui, porque não é um painel novo: é a mesma pilha flutuante, as mesmas
 * classes e o mesmo comportamento não-interactivo que o artefacto já tem. Um
 * segundo componente com moldura própria seria interface de cartas, e a
 * interface de cartas é de outro gate.
 *
 * Só texto, porque os masters não existem. Um `<img>` para uma chave de arte
 * ausente seria um pedido partido; o nome e a raridade vêm do catálogo e são o
 * que o jogador precisa de ler para saber o que ganhou.
 *
 * ## E existe porque o silêncio seria pior
 *
 * Sem isto, um checkpoint que confirmasse uma carta entregaria o item ao cofre
 * e não diria nada a ninguém - o jogador só a encontraria por acaso, dias
 * depois, num inventário. Uma queda anunciada por engano seria um defeito; uma
 * queda calada é o mesmo defeito, mais difícil de ver.
 */
function CardToast({
  drop,
  onDismiss,
}: {
  drop: CardDropWire;
  onDismiss: (dropId: string) => void;
}) {
  useEffect(() => {
    const timer = window.setTimeout(() => onDismiss(drop.dropId), VISIBLE_MS);
    return () => window.clearTimeout(timer);
  }, [drop.dropId, onDismiss]);

  return (
    <div
      className="drop-toast"
      data-rarity={drop.rarity}
      data-paint={rarityPaint(drop.rarity)}
      data-testid={`card-drop-toast-${drop.definitionId}`}
      style={rarityStyle(drop.rarity)}
      role="status"
    >
      <span className="drop-toast-text">
        <strong data-testid="card-drop-toast-name">{drop.name}</strong>
        <span data-testid="card-drop-toast-rarity">{rarityLabel(drop.rarity)}</span>
        <span className="drop-toast-source">
          {drop.fromBoss ? 'Derrubada por um boss' : 'Carta encontrada'}
        </span>
      </span>
      <button
        type="button"
        className="drop-toast-close"
        data-testid="card-drop-toast-close"
        aria-label={`Fechar aviso de ${drop.name}`}
        onClick={() => onDismiss(drop.dropId)}
      >
        ×
      </button>
    </div>
  );
}

export function CardDropToasts({
  drops,
  onDismiss,
}: {
  readonly drops: readonly CardDropWire[];
  readonly onDismiss: (dropId: string) => void;
}) {
  if (drops.length === 0) {
    return null;
  }
  return (
    <div className="drop-toasts" data-testid="card-drop-toasts" aria-live="polite">
      {drops.map((drop) => (
        <CardToast key={drop.dropId} drop={drop} onDismiss={onDismiss} />
      ))}
    </div>
  );
}
